/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import docApi from "api";
import * as stores from "stores";
import { getJsSAB } from "selectors/appSelectors";
import throwJSError from "javascript/jsEngine/utils/jsError";
import { JS_ERROR_TYPES } from "javascript/jsEngine/constants/enums";
import { logJSActionsData } from "logging";

class ThreadHostSimulator {
  init() {
    this.jsSAB = getJsSAB(stores);
    this.Uint8View = null;

    if (this.jsSAB) {
      this.Uint8View = new Uint8Array(this.jsSAB);
      this.Uint8View.fill(0);
    }

    this.busyLoopStatus = Object.freeze({
      ENABLED: 0,
      DISABLED: 1
    });
    this.doneStatus = Object.freeze({
      IN_PROGRESS: 0,
      DONE: 1
    });
    this.messageStatus = Object.freeze({
      NO_MESSAGE: 0,
      HAS_MESSAGE: 1
    });

    this.busyLoopStatusByteIndex = 0;
    this.doneByteIndex = 1;
    this.statusByteIndex = 2;
    this.lengthBytesStartIndex = 3;
    this.lengthBytesEndIndex = 4;
    this.dataStartIndex = 5;
    this.busyLoopData = {};
    this._initBusyLoopLoggingData();
  }

  _initBusyLoopLoggingData() {
    this.busyLoopData = { apiConsumedMaxBusyLoopTime: "", maxBusylooptime: 0, cumulativeBusyLoopTime: 0 };
  }

  set jsActionsBusyLoopLoggingData(data) {
    this.busyLoopData = data;
  }

  get jsActionsBusyLoopLoggingData() {
    return this.busyLoopData;
  }

  resetJSActionsBusyLoopLoggingData() {
    this._initBusyLoopLoggingData();
  }

  get _busyWaitTimeout() {
    return 13 * 1000; // see different times on based of browsers
  }

  get _isDoneMessage() {
    return (
      this.Uint8View[this.doneByteIndex] === this.doneStatus.DONE &&
      this.Uint8View[this.statusByteIndex] === this.messageStatus.NO_MESSAGE
    );
  }

  get _isThereAMessage() {
    return this.Uint8View[this.statusByteIndex] === this.messageStatus.HAS_MESSAGE;
  }

  get isBusyLoopDisabled() {
    return !this.Uint8View || this.Uint8View[this.busyLoopStatusByteIndex] === this.busyLoopStatus.DISABLED;
  }

  _captureBusyLoopStats(busyLoopTime, api) {
    const jsActionsBusyLoopLoggingData = this.jsActionsBusyLoopLoggingData;

    const { apiConsumedMaxBusyLoopTime, maxBusylooptime } =
      jsActionsBusyLoopLoggingData.maxBusylooptime < busyLoopTime
        ? { apiConsumedMaxBusyLoopTime: api, maxBusylooptime: busyLoopTime }
        : jsActionsBusyLoopLoggingData;

    this.jsActionsBusyLoopLoggingData = {
      apiConsumedMaxBusyLoopTime,
      maxBusylooptime,
      cumulativeBusyLoopTime: jsActionsBusyLoopLoggingData.cumulativeBusyLoopTime + busyLoopTime
    };
  }

  _processMessage() {
    const utf8decoder = new TextDecoder();
    const length = this.Uint8View[this.lengthBytesStartIndex] * 256 + this.Uint8View[this.lengthBytesEndIndex];
    const resp = utf8decoder.decode(this.Uint8View.slice(this.dataStartIndex, length + this.dataStartIndex));
    this.Uint8View[this.statusByteIndex] = this.messageStatus.NO_MESSAGE;
    return resp;
  }

  postToWorkerAndWait = (ajsApiName, params) => {
    if (this.isBusyLoopDisabled) {
      return throwJSError(JS_ERROR_TYPES.GENERAL_ERROR);
    }

    params.apiName = ajsApiName;
    docApi.jsSpecificAPICall(params);

    let apiResponse = "";
    const busyWaitStartTime = Date.now();
    while (!this._isDoneMessage) {
      if (this.isBusyLoopDisabled) {
        logJSActionsData({ switchJSOccuredInBusyLoop: true, ajsApi: ajsApiName });
        return throwJSError(JS_ERROR_TYPES.GENERAL_ERROR);
      }

      const currentTime = Date.now();
      const busyLoopTime = currentTime - busyWaitStartTime;

      if (busyLoopTime >= this._busyWaitTimeout) {
        logJSActionsData({ breachedBusyLoopWaitTimeout: true, ajsApi: ajsApiName });
        break;
      }

      if (this._isThereAMessage) {
        apiResponse += this._processMessage();
      }
    }

    this.Uint8View.fill(0);
    this._captureBusyLoopStats(Date.now() - busyWaitStartTime, ajsApiName);
    try {
      return JSON.parse(apiResponse);
    } catch (err) {
      return {};
    }
  };
}

export default new ThreadHostSimulator();
